import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionRollingDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { useTrackingState } from 'contexts/TrackingProvider';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import React, { useRef } from 'react';
import { RollingItem } from 'app-components/rolling/RollingItem';
import { RollingRelayDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';
import { useSlide } from 'ui-hooks/useSlide';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import { css } from '@emotion/react';
import { hideScrollbar } from 'styles/common';
import { isPC } from 'constants/layout';
import { Color } from '@diritto/common/style/color';

type SectionProps = {
  section: SectionRollingDto;
  profileLites: Record<number, ProfileLiteRelayDto>;
  rollings: Record<number, RollingRelayDto>;
};

const Height = 188;
const VerticalGap = 8;
const HorizontalGap = 8;

export const SectionRollings = ({
  section,
  rollings,
  profileLites,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  if (section.rollingIds.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <StyledWrapper>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
        <StyledScrollContainer ref={slideRef} onScroll={handleSlide} rowCount={section.rowCount}>
          {section.rollingIds.map((rollingId, index) => {
            const rolling = rollings[rollingId];
            if (!rolling) {
              return null;
            }

            const profile = profileLites[rolling.profileId];
            if (!profile || profile.isDeleted) {
              return null;
            }

            return (
              <StyledRollingItem
                key={rollingId}
                rolling={rolling}
                profile={profile}
                trackerExtraInfo={trackerExtraInfo}
                showMoreMenu={false}
                onClick={() =>
                  tracker.track(TrackingEventType.ClickItemOnHomeSection, {
                    sectionId: section.id,
                    sectionDesign: section.design,
                    listItemIndex: index,
                    trackerExtraInfo,
                  })
                }
                showDescLines={4}
              />
            );
          })}
        </StyledScrollContainer>
      </StyledWrapper>
    </div>
  );
};

export default SectionRollings;

const StyledRollingItem = styled(RollingItem)`
  width: 228px;
  height: ${Height}px;
  background-color: ${Color.White};
`;

const StyledWrapper = styled.div`
  position: relative;
`;

const StyledScrollContainer = styled.div(
  ({ rowCount = 1 }: { rowCount?: number }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    padding: 0 16px;
    overflow-y: hidden;
    overflow-x: scroll;
    ${hideScrollbar};
    gap: ${VerticalGap}px ${HorizontalGap}px;
    height: ${Height * rowCount + VerticalGap * (rowCount - 1)}px;
    @media (${isPC}) {
      overflow-x: hidden;
      padding: 0;
      margin: 0 15px;
    }
  `,
);
